import React, { useEffect, useState } from 'react';
import { Workflow, Loader2, Check, Circle, Minus, Link } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

// Icon mapping for effects
const EFFECT_ICONS = {
  Full: <Check className="w-5 h-5 text-green-500 mx-auto" title="Full" />,
  Partial: <Circle className="w-5 h-5 text-yellow-400 mx-auto" title="Partial" />,
  None: <Minus className="w-5 h-5 text-red-500 mx-auto" title="None" />,
};

function getCoverageColor(gain) {
  if (gain >= 4) return 'text-green-500 font-bold bg-green-200';
  if (gain === 0) return 'text-red-500 font-bold bg-red-200';
  return 'text-yellow-400 font-bold bg-yellow-200';
}

function getCellColor(effect) {
  if (effect === 'Full') return 'bg-green-200';
  if (effect === 'Partial') return 'bg-yellow-200';
  if (effect === 'None') return 'bg-red-200';
  return '';
}

const CorrelationTab = ({ scanId }) => {
  const [correlationData, setCorrelationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tokensUsed, setTokensUsed] = useState(null);
  const [costUsd, setCostUsd] = useState(null);

  useEffect(() => {
    const fetchCachedCorrelation = async () => {
      setLoading(true);
      setError(null);
      setCached(false);
      try {
        const cachedResp = await fetch(ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Correlation"), { method: 'POST', credentials: 'include' });
        if (cachedResp.status === 200) {
          const cachedJson = await cachedResp.json();
          if (cachedJson && cachedJson.response) {
            const parsed = JSON.parse(cachedJson.response);
            setCorrelationData(parsed);
            setCached(true);
            setLastUpdated(cachedJson.lastUpdated || null);
            setTokensUsed(cachedJson.tokens_used || null);
            setCostUsd(cachedJson.cost_usd || null);
            setLoading(false);
            return;
          }
        }
        setCorrelationData(null);
        setCached(false);
      } catch (err) {
        setCorrelationData(null);
        setCached(false);
      } finally {
        setLoading(false);
      }
    };
    fetchCachedCorrelation();
  }, [scanId]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setCached(false);
    try {
      const response = await fetch(ApiNames.AI_HUB_CORRELATION(scanId, "default"), { method: 'POST', credentials: 'include' });
      if (!response.ok) throw new Error('Failed to generate Correlation analysis.');
      const json = await response.json();
      const parsed = JSON.parse(json.response);
      setCorrelationData(parsed);
      setTokensUsed(json.tokens_used || null);
      setCostUsd(json.cost_usd || null);
      setLastUpdated(json.lastUpdated || null);
      setCached(false);
    } catch (err) {
      setError('Unable to generate Correlation analysis. Please try again.');
      setCorrelationData(null);
    } finally {
      setLoading(false);
    }
  };

  // Table rendering
  const renderMatrixTable = () => {
    if (!correlationData || !correlationData.frameworks || !correlationData.fixes) return null;
    return (
      <div className="overflow-x-auto">
        <table className="matrix-table min-w-full border-separate border-spacing-1 text-sm">
          <thead>
            <tr>
              <th className="px-2 py-2 bg-base-200 sticky left-0 z-10">Control Fix</th>
              {correlationData.frameworks.map((fw, idx) => (
                <th key={fw} className="px-2 py-2 text-xs font-bold text-center bg-base-200">{fw}</th>
              ))}
              <th className="px-2 py-2 text-xs font-bold text-center bg-base-200">Coverage Gain</th>
            </tr>
          </thead>
          <tbody>
            {correlationData.fixes.map((fix, i) => (
              <tr key={i} className="hover:bg-base-100">
                <td className="px-2 py-2 font-medium bg-base-100 sticky left-0 z-10 min-w-[220px] max-w-xs align-top">{fix.control}</td>
                {fix.frameworkEffects.map((effect, j) => (
                  <td key={j} className={`px-2 py-2 text-center align-middle rounded ${getCellColor(effect)}`}>
                    {EFFECT_ICONS[effect] || effect}
                  </td>
                ))}
                <td className={`px-2 py-2 text-center align-middle rounded ${getCoverageColor(fix.coverageGain)}`}>+{fix.coverageGain}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="w-full max-w-6xl">
        <div className="card-header flex items-center justify-between mb-2">
          <div className="card-title flex items-center gap-2">
            <Workflow className="w-6 h-6 text-blue-400" />
            <h3 className="font-bold text-lg">Cross-Framework Control Correlation Matrix</h3>
          </div>
          <span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">
            <Link className="w-4 h-4 mr-1" />18 Frameworks
          </span>
        </div>
        <p className="text-gray-400 mb-6 text-sm mt-2">
          AI maps overlapping controls across all 18 frameworks, showing percentage coverage gains from single fixes. Use this matrix to prioritize actions that maximize your compliance coverage.
        </p>
        <div className="w-full flex flex-col items-center">
          {!cached && !correlationData && !loading && !error && (
            <div className="flex flex-col items-center py-12">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
                onClick={handleGenerate}
                disabled={loading || !scanId}
              >
                Generate Correlation Matrix
              </button>
            </div>
          )}
          {loading && (
            <div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
              <Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
              <span className="text-gray-400">Generating Correlation analysis...</span>
            </div>
          )}
          {error && !loading && (
            <div className="ai-response text-center text-red-400 py-12">{error}</div>
          )}
          {correlationData && !loading && !error && (
            <>
              <div className="mb-6 w-full animate-fadeIn">
                {renderMatrixTable()}
              </div>
              <div className="ai-response bg-base-100 rounded-xl shadow-xl p-6 animate-fadeIn mb-4 w-full">
                <h4 className="font-bold text-lg mb-2">Correlation Analysis</h4>
                <p className="mb-4 text-black-400" style={{ whiteSpace: 'pre-line' }}>{correlationData.analysis}</p>
                {tokensUsed && <p className="mb-2 text-gray-400">Tokens used: {tokensUsed}. Cost: ${costUsd}.</p>}
                {cached && lastUpdated && (
                  <p className="mb-2 text-gray-400">Viewing cached Correlation analysis. Last updated: {lastUpdated}.</p>
                )}
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
                  onClick={handleGenerate}
                  disabled={loading || !scanId}
                >
                  Regenerate Correlation Analysis
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorrelationTab;